/**
 * Slow Rib — Color Utilities
 * Used server-side (provisioning) and client-side (TeamThemeProvider).
 */

/** Parse "#RRGGBB" or "#RGB" to [r, g, b] 0–255 */
function parseHex(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  if (h.length === 3) {
    return [
      parseInt(h[0] + h[0], 16),
      parseInt(h[1] + h[1], 16),
      parseInt(h[2] + h[2], 16),
    ];
  }
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

/** Relative luminance per WCAG 2.1 */
function luminance(hex: string): number {
  const [r, g, b] = parseHex(hex).map(c => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Returns the best foreground color (white or near-black) for text/icons
 * sitting on top of `background`.
 * Rule: if luminance < 0.35 → white; otherwise → #0B1220 (ink-1).
 */
export function getOnColor(background: string): string {
  return luminance(background) < 0.35 ? '#FFFFFF' : '#0B1220';
}

/**
 * Mix `hex` with white at `amount` (0–1, where 1 = pure white).
 * Returns the mixed hex string.
 */
export function mixWithWhite(hex: string, amount: number): string {
  const [r, g, b] = parseHex(hex);
  const mix = (c: number) => Math.round(c + (255 - c) * amount);
  return '#' + [mix(r), mix(g), mix(b)].map(v => v.toString(16).padStart(2, '0')).join('');
}

/**
 * WCAG contrast ratio between two colors (≥ 4.5:1 = AA pass).
 */
export function wcagContrast(fg: string, bg: string): number {
  const l1 = luminance(fg);
  const l2 = luminance(bg);
  const [light, dark] = l1 > l2 ? [l1, l2] : [l2, l1];
  return (light + 0.05) / (dark + 0.05);
}

export interface TeamTheme {
  primary:      string;
  primaryOn:    string;   // computed — foreground on primary
  primaryTint:  string;   // computed — 92% white mix (8% primary)
  secondary:    string;
  secondaryOn:  string;   // computed — foreground on secondary
}

/**
 * Compute the full TeamTheme from raw hex values.
 * Call this once when a team is provisioned; store the result.
 */
export function buildTeamTheme(primary: string, secondary: string): TeamTheme {
  return {
    primary,
    primaryOn:   getOnColor(primary),
    primaryTint: mixWithWhite(primary, 0.92),
    secondary,
    secondaryOn: getOnColor(secondary),
  };
}
